import api from "../api";

const inspectionsService = {
  // Get building managers
  getBuildingManagers: async () => {
    try {
      const response = await api.get("/users/building-managers");
      return response.data;
    } catch (error) {
      console.error("Error fetching building managers:", error);
      throw error;
    }
  },

  // Get inspection types
  getInspectionTypes: async () => {
    try {
      const response = await api.get("/inspections/types");
      return response.data;
    } catch (error) {
      console.error("Error fetching inspection types:", error);
      throw error;
    }
  },

  // Search properties (for inspections - uses /all/search endpoint like Laravel)
  searchProperties: async (query) => {
    try {
      // Laravel uses /api/properties/all/search/{query} for inspections
      // Backend expects query as URL parameter, not query string
      const response = await api.get(
        `/properties/all/search/${encodeURIComponent(query)}`
      );
      return response.data;
    } catch (error) {
      console.error("Error searching properties:", error);
      throw error;
    }
  },

  // Create single inspection
  createInspection: async (formData) => {
    try {
      const response = await api.post("/inspections", formData);
      return response.data;
    } catch (error) {
      console.error("Error creating inspection:", error);
      throw error;
    }
  },

  // Create inspections (multiple)
  createInspections: async (formData) => {
    try {
      const response = await api.post("/inspections/store-many", formData);
      return response.data;
    } catch (error) {
      console.error("Error creating inspections:", error);
      throw error;
    }
  },

  // Get inspections list
  getInspections: async (params = {}) => {
    try {
      const response = await api.get("/inspections", { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching inspections:", error);
      throw error;
    }
  },

  // Get inspections index with all data
  getInspectionsIndex: async () => {
    try {
      const response = await api.get("/inspections/index");
      return response.data;
    } catch (error) {
      console.error("Error fetching inspections index:", error);
      throw error;
    }
  },

  // Get single inspection
  getInspection: async (id) => {
    try {
      const response = await api.get(`/inspections/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching inspection:", error);
      throw error;
    }
  },

  // Update inspection
  updateInspection: async (id, formData) => {
    try {
      const response = await api.put(`/inspections/${id}`, formData);
      return response.data;
    } catch (error) {
      console.error("Error updating inspection:", error);
      throw error;
    }
  },

  // Delete inspection
  deleteInspection: async (id) => {
    try {
      const response = await api.delete(`/inspections/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting inspection:", error);
      throw error;
    }
  },

  // Get inspections by property
  getInspectionsByProperty: async (propertyId) => {
    try {
      const response = await api.get(`/inspections/property/${propertyId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching inspections by property:", error);
      throw error;
    }
  },

  // Get inspections by building manager
  getInspectionsByBuildingManager: async (userId) => {
    try {
      const response = await api.get(`/inspections/building-manager/${userId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching inspections by building manager:", error);
      throw error;
    }
  },

  // Export inspections
  exportInspections: async (format = "csv", params = {}) => {
    try {
      const response = await api.get(`/inspections/export/${format}`, {
        params,
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      console.error("Error exporting inspections:", error);
      throw error;
    }
  },

  // Get calendar events for a date range (optimized)
  getBuildingManagerCalendarEvents: async (buildingManagerId, startDate, endDate) => {
    try {
      const response = await api.get(
        `/inspections/building-manager/${buildingManagerId}/calendar-events`,
        { params: { start: startDate, end: endDate } }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching calendar events:", error);
      throw error;
    }
  },

  // Get paginated inspections for building manager table
  getBuildingManagerInspectionsPaginated: async (buildingManagerId, { page = 1, limit = 10, search = '', status = 'all' } = {}) => {
    try {
      const response = await api.get(
        `/inspections/building-manager/${buildingManagerId}/inspections`,
        { params: { page, limit, search, status } }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching paginated inspections:", error);
      throw error;
    }
  },

  // Get building manager calendar data
  getBuildingManagerCalendar: async (buildingManagerId) => {
    try {
      const response = await api.get(
        `/inspections/building-manager/${buildingManagerId}/calendar`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching building manager calendar:", error);
      throw error;
    }
  },

  // Get email reminder form data
  getEmailReminderData: async (buildingManagerId) => {
    try {
      const response = await api.get(
        `/inspections/building-manager/${buildingManagerId}/email-reminder/create`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching email reminder data:", error);
      throw error;
    }
  },

  // Send email reminder
  sendEmailReminder: async (buildingManagerId, formData) => {
    try {
      // If formData is FormData, remove Content-Type header to let axios set it with boundary
      const config =
        formData instanceof FormData
          ? { headers: { "Content-Type": undefined } }
          : {};

      const response = await api.post(
        `/inspections/building-manager/${buildingManagerId}/email-reminder`,
        formData,
        config
      );
      return response.data;
    } catch (error) {
      console.error("Error sending email reminder:", error);
      throw error;
    }
  },

  // Add inspection from calendar drag & drop
  addFromCalendar: async (data) => {
    try {
      const response = await api.post("/inspections/calendar/add", data);
      return response.data;
    } catch (error) {
      console.error("Error adding inspection from calendar:", error);
      throw error;
    }
  },

  // Get route name for a day
  getRouteForDay: async (date, buildingManagerId) => {
    try {
      const response = await api.post("/inspections/calendar/routeForDay", {
        date,
        buildingManagerId,
      });
      return response.data;
    } catch (error) {
      console.error("Error getting route for day:", error);
      throw error;
    }
  },

  // Schedule randomly
  scheduleRandomly: async (month, year, buildingManagerId) => {
    try {
      const response = await api.post("/inspections/schedule-randomly", {
        month,
        year,
        building_manager_id: buildingManagerId,
      });
      return response.data;
    } catch (error) {
      console.error("Error scheduling randomly:", error);
      throw error;
    }
  },

  // Reschedule inspection
  rescheduleInspection: async (id, start, end) => {
    try {
      const response = await api.post("/inspections/reschedule", {
        id,
        start,
        end,
      });
      return response.data;
    } catch (error) {
      console.error("Error rescheduling inspection:", error);
      throw error;
    }
  },
};

export default inspectionsService;
